import Swal from 'sweetalert2';

/**
 * ES6 Helpers Module for ERP System
 *
 * This module provides a collection of modern, tree-shakable utility functions
 * for use throughout the ERP application with Vite.
 *
 * Dependencies like jQuery and Swal are assumed to be available globally
 * or managed through the application's main entry point (e.g., app.js).
 */

/**
 * Waits for jQuery to be available before resolving a Promise.
 * @param {number} [timeout=10000] - Maximum wait time in milliseconds.
 * @param {number} [interval=50] - Check interval in milliseconds.
 * @returns {Promise<jQuery>} A Promise that resolves with the jQuery object.
 */
export const waitForJQuery = (timeout = 10000, interval = 50) => {
    return new Promise((resolve, reject) => {
        const startTime = Date.now();

        const checkJQuery = () => {
            if (typeof window.$ !== 'undefined' && typeof window.jQuery !== 'undefined') {
                // console.log(`✅ jQuery detected after ${Date.now() - startTime}ms`);
                resolve(window.$);
            } else if (Date.now() - startTime > timeout) {
                // console.error(`❌ jQuery not found after ${timeout}ms - timing out.`);
                reject(new Error(`jQuery loading timeout after ${timeout}ms`));
            } else {
                setTimeout(checkJQuery, interval);
            }
        };

        checkJQuery();
    });
};

/**
 * Dynamically loads a script and returns a Promise that resolves when the script is loaded.
 * @param {string} src - The source URL of the script to load.
 * @param {number} [timeout=5000] - Maximum wait time in milliseconds.
 * @returns {Promise<void>} A Promise that resolves when the script has loaded.
 */
export const loadScript = (src, timeout = 5000) => {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            // console.log(`✅ Script already loaded: ${src}`);
            return resolve();
        }

        // console.log(`📦 Loading script: ${src}`);
        const script = document.createElement('script');
        script.src = src;
        script.async = true;

        const timeoutId = setTimeout(() => {
            // console.error(`❌ Script loading timeout: ${src}`);
            reject(new Error(`Script loading timeout: ${src}`));
        }, timeout);

        script.onload = () => {
            clearTimeout(timeoutId);
            // console.log(`✅ Script loaded successfully: ${src}`);
            resolve();
        };

        script.onerror = () => {
            clearTimeout(timeoutId);
            // console.error(`❌ Failed to load script: ${src}`);
            reject(new Error(`Failed to load script: ${src}`));
        };

        document.head.appendChild(script);
    });
};

/**
 * Loads an array of scripts in sequence.
 * @param {string[]} scripts - An array of script URLs to load.
 * @param {number} [timeout=5000] - Maximum wait time per script.
 * @returns {Promise<void>} A Promise that resolves when all scripts are loaded.
 */
export const loadScripts = (scripts, timeout = 5000) => {
    return scripts.reduce((promise, script) => {
        return promise.then(() => loadScript(script, timeout));
    }, Promise.resolve());
};

/**
 * Initializes Select2 on a given selector with common configurations.
 * @param {string|jQuery} selector - The element selector or jQuery object.
 * @param {object} [options={}] - Custom Select2 options to merge with defaults.
 * @returns {Promise<jQuery>} A Promise that resolves with the initialized jQuery elements.
 */
export const initializeSelect2 = async (selector, options = {}) => {
    const $ = await waitForJQuery();
    await loadScript('/build/plugins/select2/select2.min.js');

    const defaultOptions = {
        width: '100%',
        placeholder: 'Select an option...',
        allowClear: true,
        escapeMarkup: (markup) => markup,
        theme: 'bootstrap-5'
    };

    const finalOptions = { ...defaultOptions, ...options };
    const $elements = $(selector);

    if ($elements.length) {
        $elements.each(function() {
            if (!$(this).hasClass('select2-hidden-accessible')) {
                $(this).select2(finalOptions);
            }
        });
        // console.log(`✅ Select2 initialized for ${$elements.length} elements`);
    }

    return $elements;
};

/**
 * Shows a SweetAlert confirmation dialog.
 * @param {object} [config={}] - Custom SweetAlert configuration.
 * @returns {Promise<SweetAlertResult>} A Promise that resolves with the user's choice.
 */
export const confirmDialog = (config = {}) => {
    const defaultConfig = {
        title: "Are you sure?",
        text: "This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, proceed!",
        cancelButtonText: "No, cancel!",
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        reverseButtons: true
    };

    const finalConfig = { ...defaultConfig, ...config };

    if (typeof Swal !== 'undefined') {
        return Swal.fire(finalConfig);
    }

    // console.warn('⚠️ SweetAlert not available, using native confirm.');
    const result = confirm(finalConfig.text);
    return Promise.resolve({ isConfirmed: result, value: result });
};

/**
 * A reusable alert function factory.
 * @private
 */
const _createAlert = (defaultIcon, defaultColor) => (title, text = '', options = {}) => {
    const config = {
        icon: defaultIcon,
        title,
        text,
        confirmButtonText: 'OK',
        confirmButtonColor: defaultColor,
        ...options
    };

    if (typeof Swal !== 'undefined') {
        return Swal.fire(config);
    }

    alert(`${defaultIcon.toUpperCase()}: ${title}${text ? `\n${text}` : ''}`);
    return Promise.resolve();
};

/** Shows a success-themed alert. */
export const successAlert = _createAlert('success', '#28a745');
/** Shows an error-themed alert. */
export const errorAlert = _createAlert('error', '#dc3545');
/** Shows an info-themed alert. */
export const infoAlert = _createAlert('info', '#17a2b8');
/** Shows a warning-themed alert. */
export const warningAlert = _createAlert('warning', '#ffc107');

/**
 * Creates a debounced function that delays invoking `func` until after `wait` milliseconds.
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @returns {Function} The new debounced function.
 */
export const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
        const later = () => {
            clearTimeout(timeout);
            func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

/**
 * Creates a throttled function that only invokes `func` at most once per every `limit` milliseconds.
 * @param {Function} func - The function to throttle.
 * @param {number} limit - The time limit in milliseconds.
 * @returns {Function} The new throttled function.
 */
export const throttle = (func, limit) => {
    let inThrottle;
    return (...args) => {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
};

/**
 * Initializes page functionality that depends on the DOM and jQuery.
 * @param {Function} callback - The function to execute, which will receive jQuery as an argument.
 * @param {object} [options={}] - Configuration options.
 * @returns {Promise<any>} A Promise that resolves with the result of the callback.
 */
export const initializePage = (callback, options = {}) => {
    const config = {
        timeout: 10000,
        interval: 50,
        waitForDOM: true,
        ...options
    };

    const executeCallback = async () => {
        try {
            const $ = await waitForJQuery(config.timeout, config.interval);
            // console.log('🚀 Page initialization started');
            const result = await callback($);
            // console.log('🎉 Page initialization completed');
            return result;
        } catch (error) {
            // console.error('💥 Page initialization failed:', error);
            throw error;
        }
    };

    if (config.waitForDOM) {
        return new Promise((resolve, reject) => {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => executeCallback().then(resolve).catch(reject));
            } else {
                executeCallback().then(resolve).catch(reject);
            }
        });
    }

    return executeCallback();
};

/**
 * Shows a toast notification.
 * @param {string} message - The message to display.
 * @param {string} [type='info'] - The type of toast ('success', 'error', 'warning', 'info').
 * @param {object} [options={}] - Additional SweetAlert options.
 */
export const showToast = (message, type = 'info', options = {}) => {
    const config = {
        position: 'top-end',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
        toast: true,
        icon: type,
        title: message,
        ...options
    };

    if (typeof Swal !== 'undefined') {
        Swal.fire(config);
    } else {
        // console.log(`${type.toUpperCase()}: ${message}`);
    }
};

/**
 * Formats a number as a currency string.
 * @param {number} amount - The amount to format.
 * @param {string} [currency='EGP'] - The currency code.
 * @param {string} [locale='en-EG'] - The locale for formatting.
 * @returns {string} The formatted currency string.
 */
export const formatCurrency = (amount, currency = 'EGP', locale = 'en-EG') => {
    try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch (error) {
        return `${currency} ${Number(amount).toFixed(2)}`;
    }
};

/**
 * Formats a number with thousands separators.
 * @param {number} num - The number to format.
 * @param {number} [decimals=2] - The number of decimal places.
 * @returns {string} The formatted number string.
 */
export const formatNumber = (num, decimals = 2) => {
    try {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(num);
    } catch (error) {
        return Number(num).toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
};

/**
 * Formats a value as a percentage string.
 * @param {number} value - The value to format (e.g., 50 for 50%).
 * @param {number} [decimals=1] - The number of decimal places.
 * @returns {string} The formatted percentage string.
 */
export const formatPercentage = (value, decimals = 1) => {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'percent',
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(value / 100);
    } catch (error) {
        return `${Number(value).toFixed(decimals)}%`;
    }
};

/**
 * Formats a date into a readable string.
 * @param {Date|string} date - The date to format.
 * @param {string} [locale='en-US'] - The locale for formatting.
 * @param {object} [options={}] - Custom Intl.DateTimeFormat options.
 * @returns {string} The formatted date string.
 */
export const formatDate = (date, locale = 'en-US', options = {}) => {
    const defaultOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    const finalOptions = { ...defaultOptions, ...options };
    const dateObj = date instanceof Date ? date : new Date(date);

    try {
        return new Intl.DateTimeFormat(locale, finalOptions).format(dateObj);
    } catch (error) {
        return dateObj.toLocaleDateString();
    }
};

/**
 * Formats a time into a readable string.
 * @param {Date|string} time - The time to format.
 * @param {string} [locale='en-US'] - The locale for formatting.
 * @param {object} [options={}] - Custom Intl.DateTimeFormat options.
 * @returns {string} The formatted time string.
 */
export const formatTime = (time, locale = 'en-US', options = {}) => {
    const defaultOptions = { hour: '2-digit', minute: '2-digit', second: '2-digit' };
    const finalOptions = { ...defaultOptions, ...options };
    const timeObj = time instanceof Date ? time : new Date(time);

    try {
        return new Intl.DateTimeFormat(locale, finalOptions).format(timeObj);
    } catch (error) {
        return timeObj.toLocaleTimeString();
    }
};

/**
 * Validates an email address format.
 * @param {string} email - The email to validate.
 * @returns {boolean} True if the email format is valid.
 */
export const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * Validates an Egyptian phone number format.
 * @param {string} phone - The phone number to validate.
 * @returns {boolean} True if the phone format is valid.
 */
export const isValidPhone = (phone) => {
    const cleanPhone = String(phone).replace(/\D/g, '');
    const patterns = [/^01[0125]\d{8}$/, /^02\d{8}$/, /^03\d{7}$/];
    return patterns.some(pattern => pattern.test(cleanPhone));
};

/**
 * Validates a URL format.
 * @param {string} url - The URL to validate.
 * @returns {boolean} True if the URL is valid.
 */
export const isValidUrl = (url) => {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

/**
 * Validates a credit card number using the Luhn algorithm.
 * @param {string} cardNumber - The credit card number to validate.
 * @returns {boolean} True if the number is valid.
 */
export const isValidCreditCard = (cardNumber) => {
    const cleanNumber = String(cardNumber).replace(/\D/g, '');
    if (cleanNumber.length < 13 || cleanNumber.length > 19) return false;

    let sum = 0;
    let shouldDouble = false;
    for (let i = cleanNumber.length - 1; i >= 0; i--) {
        let digit = parseInt(cleanNumber[i], 10);
        if (shouldDouble) {
            digit *= 2;
            if (digit > 9) digit -= 9;
        }
        sum += digit;
        shouldDouble = !shouldDouble;
    }
    return sum % 10 === 0;
};

/**
 * Displays a loading indicator using SweetAlert.
 * @param {string} [message='Loading...'] - The message to display.
 * @returns {{hide: Function, update: Function}} An object with methods to hide or update the loader.
 */
export const showLoading = (message = 'Loading...') => {
    if (typeof Swal !== 'undefined') {
        Swal.fire({
            title: message,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => Swal.showLoading(),
        });
        return {
            hide: () => Swal.close(),
            update: (newMessage) => Swal.update({ title: newMessage }),
        };
    }

    // console.log(message);
    return {
        hide: () => {
            // console.log('Loading complete.');
        },
        update: (newMessage) => {
            // console.log(newMessage);
        },
    };
};

/**
 * Copies text to the user's clipboard.
 * @param {string} text - The text to copy.
 * @returns {Promise<void>} A Promise that resolves when the text is copied.
 */
export const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
    }

    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    return new Promise((resolve, reject) => {
        try {
            document.execCommand('copy');
            resolve();
        } catch (err) {
            reject(err);
        } finally {
            textArea.remove();
        }
    });
};

/**
 * Gets URL search parameters as an object.
 * @param {string} [url=window.location.href] - The URL to parse.
 * @returns {object} An object containing the URL's search parameters.
 */
export const getUrlParams = (url = window.location.href) => {
    const urlObj = new URL(url);
    return Object.fromEntries(urlObj.searchParams.entries());
};

/**
 * Sets or updates a URL search parameter.
 * @param {string} key - The parameter key.
 * @param {string} value - The parameter value.
 * @param {boolean} [pushState=true] - If true, adds a new entry to the browser's history.
 */
export const setUrlParam = (key, value, pushState = true) => {
    const url = new URL(window.location);
    url.searchParams.set(key, value);
    const historyMethod = pushState ? 'pushState' : 'replaceState';
    window.history[historyMethod]({}, '', url);
};

/**
 * Removes a URL search parameter.
 * @param {string} key - The parameter key to remove.
 * @param {boolean} [pushState=true] - If true, adds a new entry to the browser's history.
 */
export const removeUrlParam = (key, pushState = true) => {
    const url = new URL(window.location);
    url.searchParams.delete(key);
    const historyMethod = pushState ? 'pushState' : 'replaceState';
    window.history[historyMethod]({}, '', url);
};

/**
 * Generates a random string of a given length.
 * @param {number} [length=10] - The length of the string.
 * @param {string} [chars='...'] - The character set to use.
 * @returns {string} The generated random string.
 */
export const randomString = (length = 10, chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789') => {
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

/**
 * Generates a Version 4 UUID.
 * @returns {string} The UUID.
 */
export const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

/**
 * Deep clones an object or array.
 * @param {T} obj - The object or array to clone.
 * @returns {T} The cloned object or array.
 * @template T
 */
export const deepClone = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (Array.isArray(obj)) return obj.map(item => deepClone(item));

    const cloned = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            cloned[key] = deepClone(obj[key]);
        }
    }
    return cloned;
};

/**
 * Checks if a variable is empty (null, undefined, empty string, empty array, or empty object).
 * @param {any} value - The value to check.
 * @returns {boolean} True if the value is empty.
 */
export const isEmpty = (value) => {
    if (value == null) return true;
    if (Array.isArray(value) || typeof value === 'string') return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
};

/**
 * Capitalizes the first letter of a string.
 * @param {string} str - The string to capitalize.
 * @returns {string} The capitalized string.
 */
export const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Converts a string to title case.
 * @param {string} str - The string to convert.
 * @returns {string} The title-cased string.
 */
export const toTitleCase = (str) => {
    if (!str) return '';
    return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

/**
 * Truncates a string to a specified length.
 * @param {string} str - The string to truncate.
 * @param {number} length - The maximum length.
 * @param {string} [suffix='...'] - The suffix to add if the string is truncated.
 * @returns {string} The truncated string.
 */
export const truncate = (str, length, suffix = '...') => {
    if (!str || str.length <= length) return str;
    return str.substring(0, length) + suffix;
};

/**
 * Converts a number of bytes to a human-readable format.
 * @param {number} bytes - The number of bytes.
 * @param {number} [decimals=2] - The number of decimal places.
 * @returns {string} The human-readable file size.
 */
export const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

/**
 * Pauses execution for a specified number of milliseconds.
 * @param {number} ms - The number of milliseconds to sleep.
 * @returns {Promise<void>} A Promise that resolves after the delay.
 */
export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Retries an async function with exponential backoff.
 * @param {Function} fn - The async function to retry.
 * @param {number} [maxRetries=3] - The maximum number of retries.
 * @param {number} [baseDelay=1000] - The base delay in milliseconds.
 * @returns {Promise<any>} A Promise that resolves with the result of the function.
 */
export const retry = async (fn, maxRetries = 3, baseDelay = 1000) => {
    let lastError;
    for (let i = 0; i <= maxRetries; i++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;
            if (i === maxRetries) throw lastError;
            await sleep(baseDelay * Math.pow(2, i));
        }
    }
};

/**
 * Generates a random number for the encryption step.
 */
export let cryptStep = Math.floor(Math.random() * 3);
window.cryptStep = cryptStep;

/**
 * Generates a random number for the encryption offset.
 */
export let cryptOffset = Math.floor(Math.random() * 26);
window.cryptOffset = cryptOffset;

/**
 * Encrypts a phone number string by shifting its digits based on a step and offset.
 * @param {string|number} number - The phone number to encrypt.
 * @param {number} step - The encryption step.
 * @param {number} offset - The encryption offset.
 * @returns {string} The encrypted string.
 */
export function cryptPhone(number, step, offset) {
    const alphabet = 'abcdefghijklmnopqrstuvwxyz'.split('');

    const string = String(number).split("");
    const crypted = string.map((letter) => {
        const digit = parseInt(letter, 10);
        if (isNaN(digit)) {
            return letter; // Return non-digit characters as-is
        }
        const pointer = digit + step + offset;
        return alphabet[pointer % 26];
    });

    return crypted.join('');
}

/**
 * Bootstrap 5 Modal Helper Functions
 */

/**
 * Shows a Bootstrap 5 modal by its ID.
 * @param {string} modalId - The ID of the modal element.
 * @param {object} [options={}] - Bootstrap modal options.
 * @returns {object|null} The Bootstrap Modal instance or null if not found.
 */
export const showModal = (modalId, options = {}) => {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) {
        // console.error(`Modal with ID '${modalId}' not found`);
        return null;
    }

    if (typeof bootstrap === 'undefined') {
        // console.error('Bootstrap 5 is not loaded');
        return null;
    }

    const modal = new bootstrap.Modal(modalElement, options);
    modal.show();
    return modal;
};

/**
 * Hides a Bootstrap 5 modal by its ID.
 * @param {string} modalId - The ID of the modal element.
 */
export const hideModal = (modalId) => {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) {
        // console.error(`Modal with ID '${modalId}' not found`);
        return;
    }

    const modal = bootstrap.Modal.getInstance(modalElement);
    if (modal) {
        modal.hide();
    }
};

/**
 * Loads content from a URL and displays it in a modal.
 * @param {string} modalId - The ID of the modal element.
 * @param {string} url - The URL to load content from.
 * @param {string} [contentSelector='.modal-content'] - The selector for the content container.
 * @param {object} [modalOptions={}] - Bootstrap modal options.
 * @param {object} [fetchOptions={}] - Fetch API options.
 * @returns {Promise<object|null>} Promise that resolves with the modal instance.
 */
export const showModalWithContent = async (modalId, url, contentSelector = '.modal-content', modalOptions = {}, fetchOptions = {}) => {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) {
        // console.error(`Modal with ID '${modalId}' not found`);
        return null;
    }

    const contentElement = modalElement.querySelector(contentSelector);
    if (!contentElement) {
        // console.error(`Content selector '${contentSelector}' not found in modal '${modalId}'`);
        return null;
    }

    try {
        // Show loading state
        contentElement.innerHTML = '<div class="text-center p-4"><div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div></div>';

        // Fetch content
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'text/html',
                ...fetchOptions.headers
            },
            ...fetchOptions
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const html = await response.text();
        contentElement.innerHTML = html;

        // Show modal
        return showModal(modalId, modalOptions);

    } catch (error) {
        // console.error('Error loading modal content:', error);
        contentElement.innerHTML = `
            <div class="alert alert-danger m-3">
                <h5>Error Loading Content</h5>
                <p>Failed to load content from: ${url}</p>
                <small class="text-muted">${error.message}</small>
            </div>
        `;
        return showModal(modalId, modalOptions);
    }
};

/**
 * jQuery-compatible version of showModalWithContent for backward compatibility.
 * @param {string} modalId - The ID of the modal element.
 * @param {string} url - The URL to load content from.
 * @param {string} [contentSelector='.modal-content'] - The selector for the content container.
 * @param {object} [modalOptions={}] - Bootstrap modal options.
 * @returns {Promise<object|null>} Promise that resolves with the modal instance.
 */
export const loadModalContent = async (modalId, url, contentSelector = '.modal-content', modalOptions = {}) => {
    // Try jQuery approach first (for compatibility)
    if (typeof window.$ !== 'undefined') {
        const $modal = window.$(`#${modalId}`);
        const $content = $modal.find(contentSelector);

        if ($modal.length && $content.length) {
            return new Promise((resolve) => {
                $content.load(url, function(response, status, xhr) {
                    if (status === 'error') {
                        // console.error(`Error loading content: ${xhr.status} ${xhr.statusText}`);
                        $content.html(`
                            <div class="alert alert-danger m-3">
                                <h5>Error Loading Content</h5>
                                <p>Failed to load content from: ${url}</p>
                                <small class="text-muted">${xhr.status} ${xhr.statusText}</small>
                            </div>
                        `);
                    }
                    const modal = showModal(modalId, modalOptions);
                    resolve(modal);
                });
            });
        }
    }

    // Fallback to fetch approach
    return showModalWithContent(modalId, url, contentSelector, modalOptions);
};

/**
 * Toggles a Bootstrap 5 modal by its ID.
 * @param {string} modalId - The ID of the modal element.
 * @param {object} [options={}] - Bootstrap modal options.
 */
export const toggleModal = (modalId, options = {}) => {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) {
        // console.error(`Modal with ID '${modalId}' not found`);
        return;
    }

    const existingModal = bootstrap.Modal.getInstance(modalElement);
    if (existingModal) {
        existingModal.toggle();
    } else {
        showModal(modalId, options);
    }
};

/**
 * Gets the Bootstrap Modal instance for a given modal ID.
 * @param {string} modalId - The ID of the modal element.
 * @returns {object|null} The Bootstrap Modal instance or null.
 */
export const getModalInstance = (modalId) => {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) {
        return null;
    }
    return bootstrap.Modal.getInstance(modalElement);
};

/**
 * A pre-configured Toast notification component using SweetAlert.
 */
export const Toast = Swal.mixin({
    toast: true,
    position: 'center',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
});

/**
 * Handles and displays errors from API responses using Toast notifications.
 * @param {object} error - The error object from an API call.
 */
export function handleResponseError(error) {
    if (error && error.errors && typeof error.errors === 'object') {
        Object.values(error.errors).flat().forEach(message => {
            Toast.fire({
                icon: 'error',
                title: message
            });
        });
    } else if (error && error.message) {
        Toast.fire({
            icon: 'error',
            title: error.message
        });
    } else {
        Toast.fire({
            icon: 'error',
            title: 'An unknown error occurred.'
        });
    }
}

/**
 * Application-wide configuration.
 */
export const AppConfig = {
    userID: document.querySelector('meta[name="user-id"]')?.getAttribute('content') || null,
};

/**
 * Sets date range variables on the global window object.
 * Note: This is an exception to module purity for compatibility with legacy code.
 * @param {string} name - The prefix for the window variables.
 * @param {string} startDate - The start date.
 * @param {string} endDate - The end date.
 */
export function initializationDateUpdateRange(name, startDate, endDate) {
    window[`${name}Start`] = startDate;
    window[`${name}End`] = endDate;
}

// Create ERPHelpers object with all functions for global access
const ERPHelpers = {
    waitForJQuery,
    loadScript,
    loadScripts,
    initializeSelect2,
    confirmDialog,
    successAlert,
    errorAlert,
    infoAlert,
    warningAlert,
    debounce,
    throttle,
    initializePage,
    showToast,
    formatCurrency,
    formatNumber,
    formatPercentage,
    formatDate,
    formatTime,
    isValidEmail,
    isValidPhone,
    isValidUrl,
    isValidCreditCard,
    showLoading,
    copyToClipboard,
    getUrlParams,
    setUrlParam,
    removeUrlParam,
    randomString,
    generateUUID,
    deepClone,
    isEmpty,
    capitalize,
    toTitleCase,
    truncate,
    formatBytes,
    sleep,
    retry,
    cryptPhone,
    handleResponseError,
    initializationDateUpdateRange,
    // Bootstrap 5 Modal Functions
    showModal,
    hideModal,
    showModalWithContent,
    loadModalContent,
    toggleModal,
    getModalInstance,
    // Other utilities
    Toast,
    AppConfig,
    cryptStep,
    cryptOffset,
};

// Make ERPHelpers globally available for inline scripts
if (typeof window !== 'undefined') {
    window.ERPHelpers = ERPHelpers;
}
